/**
 * Lending Report PDF Generator
 *
 * Generates a downloadable PDF from LendingReportData using jsPDF + autoTable.
 * Structure mirrors the UI's 5-tab layout: Summary, Income, Obligations, Cashflow, Behavior.
 * Charts are drawn using jsPDF drawing primitives (line, rect) to match UI visuals.
 *
 * Phase 2: Added statement period, salary classification, enriched EMIs, FOIR simulator,
 * spending breakdown, bounce events log, repayment heatmap.
 */
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { LendingReportData } from '../types/lending-report.types';
import { getMonthLabel, formatPeriod } from './lending-report-utils';

const BLUE: [number, number, number] = [59, 130, 246];
const HEAD_STYLE = { fillColor: BLUE, fontSize: 9, fontStyle: 'bold' as const };
const BODY_STYLE = { fontSize: 9 };
const MARGIN = { left: 14, right: 14 };
const CHART_LEFT = 14;
const CHART_RIGHT = 196; // pageWidth(210) - 14
const CHART_WIDTH = CHART_RIGHT - CHART_LEFT;
const CHART_HEIGHT = 50;

function fmt(val: number): string {
    return `Rs. ${val.toLocaleString('en-IN')}`;
}

function getY(doc: any): number {
    return (doc as any).lastAutoTable?.finalY ?? 15;
}

function ensureFit(doc: jsPDF, y: number, needed: number): number {
    if (y + needed > 280) { doc.addPage(); return 15; }
    return y;
}

function sectionHeader(doc: jsPDF, title: string, y: number): number {
    y = ensureFit(doc, y, 70);
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(55, 65, 81);
    doc.text(title, 14, y);
    doc.setTextColor(0);
    return y + 2;
}

function subHeader(doc: jsPDF, title: string, y: number): number {
    y = ensureFit(doc, y, 65);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(75, 85, 99);
    doc.text(title, 14, y);
    doc.setTextColor(0);
    return y + 2;
}

// ─── Chart Drawing Helpers ───

function drawBarChart(
    doc: jsPDF,
    y: number,
    data: { label: string; value: number; color?: [number, number, number] }[],
    defaultColor: [number, number, number],
    height = CHART_HEIGHT,
): number {
    if (data.length === 0) return y;
    y = ensureFit(doc, y, height + 15);

    const maxVal = Math.max(...data.map(d => d.value), 1);
    const barAreaTop = y;
    const barAreaBottom = y + height;
    const barWidth = Math.min(CHART_WIDTH / data.length - 2, 20);
    const gap = (CHART_WIDTH - barWidth * data.length) / (data.length + 1);

    // Light background
    doc.setFillColor(249, 250, 251);
    doc.rect(CHART_LEFT, barAreaTop - 2, CHART_WIDTH, height + 4, 'F');

    // Grid lines
    doc.setDrawColor(230, 230, 230);
    doc.setLineWidth(0.2);
    for (let i = 0; i <= 4; i++) {
        const gy = barAreaTop + (height * i) / 4;
        doc.line(CHART_LEFT, gy, CHART_RIGHT, gy);
    }

    // Bars
    data.forEach((d, i) => {
        const barH = (d.value / maxVal) * height;
        const x = CHART_LEFT + gap + i * (barWidth + gap);
        const color = d.color || defaultColor;
        doc.setFillColor(color[0], color[1], color[2]);
        if (barH > 0.5) {
            doc.rect(x, barAreaBottom - barH, barWidth, barH, 'F');
        }
    });

    // X-axis labels
    doc.setFontSize(6);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(120);
    data.forEach((d, i) => {
        const x = CHART_LEFT + gap + i * (barWidth + gap) + barWidth / 2;
        doc.text(d.label, x, barAreaBottom + 5, { align: 'center' });
    });
    doc.setTextColor(0);

    return barAreaBottom + 10;
}

function drawDualBarChart(
    doc: jsPDF,
    y: number,
    data: { label: string; inflow: number; outflow: number }[],
    height = CHART_HEIGHT,
): number {
    if (data.length === 0) return y;
    y = ensureFit(doc, y, height + 25);

    const legendY = y;
    const barAreaTop = y + 8;
    const maxVal = Math.max(...data.flatMap(d => [d.inflow, d.outflow]), 1);
    const barAreaBottom = barAreaTop + height;
    const pairWidth = Math.min(CHART_WIDTH / data.length - 4, 24);
    const singleBar = pairWidth / 2 - 1;
    const gap = (CHART_WIDTH - pairWidth * data.length) / (data.length + 1);

    doc.setFillColor(249, 250, 251);
    doc.rect(CHART_LEFT, barAreaTop - 2, CHART_WIDTH, height + 4, 'F');

    doc.setDrawColor(230, 230, 230);
    doc.setLineWidth(0.2);
    for (let i = 0; i <= 4; i++) {
        const gy = barAreaTop + (height * i) / 4;
        doc.line(CHART_LEFT, gy, CHART_RIGHT, gy);
    }

    // Legend
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setFillColor(99, 102, 241); // indigo for inflow
    doc.rect(CHART_LEFT, legendY, 4, 3, 'F');
    doc.setTextColor(80);
    doc.text('Inflow', CHART_LEFT + 6, legendY + 2.5);
    doc.setFillColor(209, 213, 219); // gray for outflow
    doc.rect(CHART_LEFT + 28, legendY, 4, 3, 'F');
    doc.text('Outflow', CHART_LEFT + 34, legendY + 2.5);
    doc.setTextColor(0);

    data.forEach((d, i) => {
        const x = CHART_LEFT + gap + i * (pairWidth + gap);
        const inflowH = (d.inflow / maxVal) * height;
        if (inflowH > 0.5) {
            doc.setFillColor(99, 102, 241); // indigo
            doc.rect(x, barAreaBottom - inflowH, singleBar, inflowH, 'F');
        }
        const outflowH = (d.outflow / maxVal) * height;
        if (outflowH > 0.5) {
            doc.setFillColor(209, 213, 219); // gray
            doc.rect(x + singleBar + 1, barAreaBottom - outflowH, singleBar, outflowH, 'F');
        }
        doc.setFontSize(6);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(120);
        doc.text(d.label, x + pairWidth / 2, barAreaBottom + 5, { align: 'center' });
    });
    doc.setTextColor(0);

    return barAreaBottom + 10;
}

function drawLineChart(
    doc: jsPDF,
    y: number,
    data: { label: string; value: number }[],
    color: [number, number, number],
    unit = '',
    height = CHART_HEIGHT,
): number {
    if (data.length < 2) return y;
    y = ensureFit(doc, y, height + 15);

    const maxVal = Math.max(...data.map(d => d.value), 1);
    const range = maxVal || 1;
    const chartTop = y;
    const chartBottom = y + height;

    doc.setFillColor(249, 250, 251);
    doc.rect(CHART_LEFT, chartTop - 2, CHART_WIDTH, height + 4, 'F');

    doc.setDrawColor(230, 230, 230);
    doc.setLineWidth(0.2);
    doc.setFontSize(6);
    doc.setTextColor(150);
    for (let i = 0; i <= 4; i++) {
        const gy = chartTop + (height * i) / 4;
        doc.line(CHART_LEFT + 12, gy, CHART_RIGHT, gy);
        const val = Math.round(maxVal - (maxVal * i) / 4);
        doc.text(`${val}${unit}`, CHART_LEFT + 10, gy + 1.5, { align: 'right' });
    }

    const points = data.map((d, i) => ({
        x: CHART_LEFT + 14 + (i / Math.max(data.length - 1, 1)) * (CHART_WIDTH - 16),
        y: chartTop + height - (d.value / range) * height,
    }));

    doc.setDrawColor(color[0], color[1], color[2]);
    doc.setLineWidth(0.6);
    for (let i = 1; i < points.length; i++) {
        doc.line(points[i - 1].x, points[i - 1].y, points[i].x, points[i].y);
    }

    doc.setFillColor(color[0], color[1], color[2]);
    points.forEach(p => { doc.circle(p.x, p.y, 1, 'F'); });

    doc.setFontSize(6);
    doc.setTextColor(120);
    data.forEach((d, i) => {
        doc.text(d.label, points[i].x, chartBottom + 5, { align: 'center' });
    });
    doc.setTextColor(0);
    doc.setDrawColor(0);

    return chartBottom + 10;
}

// ─── Main PDF Generator ───

export function generateLendingReportPDF(report: LendingReportData): void {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 15;

    // ─── Title ───
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Lending Report', pageWidth / 2, y, { align: 'center' });
    y += 8;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100);
    doc.text(`Consent ID: ${report.consentId}`, pageWidth / 2, y, { align: 'center' });
    y += 4;
    // Phase 2: Statement Period
    if (report.statementPeriodFrom && report.statementPeriodTo) {
        doc.text(`Statement Period: ${formatPeriod(report.statementPeriodFrom)} \u2013 ${formatPeriod(report.statementPeriodTo)}`, pageWidth / 2, y, { align: 'center' });
        y += 4;
    }
    doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, y, { align: 'center' });
    doc.setTextColor(0);
    y += 10;

    // ─── Key Metrics ───
    y = sectionHeader(doc, 'Key Metrics', y);
    autoTable(doc, {
        startY: y,
        head: [['Net Monthly Income', 'FOIR', 'Active EMIs', 'Avg Closing Balance', 'Inflow/Outflow', 'Bounce Events']],
        body: [[
            fmt(report.netMonthlyIncome),
            `${report.foir.toFixed(1)}%`,
            String(report.activeEMIs),
            fmt(report.avgClosingBalance),
            `${report.inflowOutflowRatio.toFixed(2)}x`,
            String(report.bounceEvents),
        ]],
        theme: 'grid',
        headStyles: { ...HEAD_STYLE, fontSize: 8 },
        bodyStyles: { ...BODY_STYLE, fontSize: 9, fontStyle: 'bold', halign: 'center' },
        margin: MARGIN,
    });
    y = getY(doc) + 12;

    // ═══════════════════════════════════════
    // SUMMARY TAB
    // ═══════════════════════════════════════
    y = sectionHeader(doc, 'Summary', y);

    // Risk Snapshot
    if (report.riskSnapshot.length > 0) {
        y = subHeader(doc, 'Risk Snapshot', y + 4);
        autoTable(doc, {
            startY: y,
            head: [['Signal', 'Status', 'Tag']],
            body: report.riskSnapshot.map((item) => [item.label, item.status, item.tag]),
            theme: 'grid',
            headStyles: HEAD_STYLE,
            bodyStyles: BODY_STYLE,
            margin: MARGIN,
        });
        y = getY(doc) + 8;
    }

    // FOIR Trend — chart + table
    if (report.foirTrend.length > 0) {
        y = subHeader(doc, 'FOIR Trend (12 Months)', y);
        y = drawLineChart(
            doc, y,
            report.foirTrend.map(p => ({ label: getMonthLabel(p.month || ''), value: p.foir })),
            [59, 130, 246], '%',
        );
        autoTable(doc, {
            startY: y,
            head: [['Month', 'Salary', 'EMI', 'FOIR %']],
            body: report.foirTrend.map((p) => [p.month, fmt(p.salary), fmt(p.emi), `${p.foir}%`]),
            theme: 'grid',
            headStyles: HEAD_STYLE,
            bodyStyles: BODY_STYLE,
            margin: MARGIN,
        });
        y = getY(doc) + 8;
    }

    y = subHeader(doc, `Employment Type: ${report.employmentType}`, y);
    y += 8;

    // ═══════════════════════════════════════
    // INCOME TAB
    // ═══════════════════════════════════════
    y = sectionHeader(doc, 'Income', y);

    // Phase 2: Classified Salary History (color-coded chart)
    if (report.classifiedSalaryHistory && report.classifiedSalaryHistory.length > 0) {
        y = subHeader(doc, 'Month-wise Salary Credits', y + 4);
        const salaryColorMap: Record<string, [number, number, number]> = {
            'Normal': [99, 102, 241],   // indigo/blue
            'Missed': [248, 113, 113],  // red
            'Double': [124, 58, 237],   // purple
        };
        y = drawBarChart(
            doc, y,
            report.classifiedSalaryHistory.map(e => ({
                label: getMonthLabel(e.month),
                value: e.amount,
                color: salaryColorMap[e.classification] || [99, 102, 241],
            })),
            [99, 102, 241],
        );
        autoTable(doc, {
            startY: y,
            head: [['Month', 'Amount', 'Classification']],
            body: report.classifiedSalaryHistory.map((e) => [e.month, fmt(e.amount), e.classification]),
            theme: 'grid', headStyles: HEAD_STYLE, bodyStyles: BODY_STYLE, margin: MARGIN,
        });
        y = getY(doc) + 8;
    } else if (report.monthlySalaryHistory.length > 0) {
        // Fallback to basic salary history
        y = subHeader(doc, 'Monthly Salary History', y + 4);
        y = drawBarChart(
            doc, y,
            report.monthlySalaryHistory.map(e => ({
                label: getMonthLabel(e.month || ''),
                value: e.amount ?? e.averageAmount ?? 0,
            })),
            [99, 102, 241],
        );
        autoTable(doc, {
            startY: y,
            head: [['Month', 'Amount']],
            body: report.monthlySalaryHistory.map((e) => [e.month ?? '-', fmt(e.amount ?? 0)]),
            theme: 'grid', headStyles: HEAD_STYLE, bodyStyles: BODY_STYLE, margin: MARGIN,
        });
        y = getY(doc) + 8;
    }

    // Salary Windows
    if (report.salaryWindows.length > 0) {
        y = subHeader(doc, `Detected Salary Windows (${report.salaryWindows.length})`, y);
        autoTable(doc, {
            startY: y,
            head: [['#', 'Amount', 'Window', 'Confidence', 'Counterparty']],
            body: report.salaryWindows.map((e, i) => [
                String(i + 1), fmt(e.averageAmount), `Day ${e.startDay} - ${e.endDay}`,
                `${Math.round(e.confidence * 100)}%`, e.counterparty || '-',
            ]),
            theme: 'grid', headStyles: HEAD_STYLE, bodyStyles: BODY_STYLE, margin: MARGIN,
        });
        y = getY(doc) + 8;
    }

    // Recurring Credits
    if (report.recurringCredits.length > 0) {
        y = subHeader(doc, 'Recurring Credits', y);
        autoTable(doc, {
            startY: y,
            head: [['Amount', 'Window', 'Counterparty']],
            body: report.recurringCredits.map((e) => [
                fmt(e.averageAmount), `Day ${e.startDay} - ${e.endDay}`, e.counterparty || '-',
            ]),
            theme: 'grid', headStyles: HEAD_STYLE, bodyStyles: BODY_STYLE, margin: MARGIN,
        });
        y = getY(doc) + 8;
    }

    // ═══════════════════════════════════════
    // OBLIGATIONS TAB
    // ═══════════════════════════════════════
    y = sectionHeader(doc, 'Obligations', y);

    // Phase 2: Enriched EMI Windows (with Months Left, Status, % of Income)
    if (report.enrichedEmiWindows && report.enrichedEmiWindows.length > 0) {
        y = subHeader(doc, `Active Obligations (${report.enrichedEmiWindows.length})`, y + 4);
        autoTable(doc, {
            startY: y,
            head: [['Obligation', 'EMI/Month', 'Months Left', 'Status', '% of Income']],
            body: report.enrichedEmiWindows.map((e) => [
                e.counterparty || '-',
                fmt(e.averageAmount),
                e.monthsLeft != null ? String(e.monthsLeft) : '-',
                e.status,
                `${e.percentOfIncome.toFixed(1)}%`,
            ]),
            theme: 'grid', headStyles: HEAD_STYLE, bodyStyles: BODY_STYLE, margin: MARGIN,
        });
        y = getY(doc) + 8;
    } else if (report.emiWindows.length > 0) {
        // Fallback to basic EMI windows
        y = subHeader(doc, `Active EMIs (${report.emiWindows.length})`, y + 4);
        autoTable(doc, {
            startY: y,
            head: [['Counterparty', 'Amount', 'Window', 'Confidence', 'Category']],
            body: report.emiWindows.map((e) => [
                e.counterparty || '-', fmt(e.averageAmount), `Day ${e.startDay} - ${e.endDay}`,
                `${Math.round(e.confidence * 100)}%`, e.category || '-',
            ]),
            theme: 'grid', headStyles: HEAD_STYLE, bodyStyles: BODY_STYLE, margin: MARGIN,
        });
        y = getY(doc) + 8;
    }

    // Phase 2: FOIR Impact Simulator (static table with 3 presets)
    {
        const currentTotalEMI = report.emiWindows.reduce((sum, e) => sum + e.averageAmount, 0);
        const presets = [5000, 8000, 12000];
        if (report.netMonthlyIncome > 0) {
            y = subHeader(doc, 'FOIR Impact Simulator', y);
            autoTable(doc, {
                startY: y,
                head: [['New EMI', 'Projected FOIR', 'Risk Level']],
                body: presets.map((emi) => {
                    const projected = ((currentTotalEMI + emi) / report.netMonthlyIncome) * 100;
                    const risk = projected > 50 ? 'Risky' : projected >= 40 ? 'Moderate' : 'Safe';
                    return [`Rs. ${emi.toLocaleString('en-IN')}`, `${projected.toFixed(1)}%`, risk];
                }),
                theme: 'grid', headStyles: HEAD_STYLE, bodyStyles: BODY_STYLE, margin: MARGIN,
            });
            y = getY(doc) + 8;
        }
    }

    // Monthly EMI History
    if (report.monthlyEmiHistory.length > 0) {
        y = subHeader(doc, 'Monthly EMI History', y);
        y = drawBarChart(
            doc, y,
            report.monthlyEmiHistory.map(e => ({
                label: getMonthLabel(e.month || ''),
                value: e.amount ?? e.averageAmount ?? 0,
            })),
            [248, 113, 113],
        );
        autoTable(doc, {
            startY: y,
            head: [['Month', 'Amount']],
            body: report.monthlyEmiHistory.map((e) => [e.month ?? '-', fmt(e.amount ?? 0)]),
            theme: 'grid', headStyles: HEAD_STYLE, bodyStyles: BODY_STYLE, margin: MARGIN,
        });
        y = getY(doc) + 8;
    }

    // Credit Card Payments
    if (report.creditCardPayments.length > 0) {
        y = subHeader(doc, 'Credit Card Payments', y);
        autoTable(doc, {
            startY: y,
            head: [['Amount', 'Window']],
            body: report.creditCardPayments.map((e) => [fmt(e.averageAmount), `Day ${e.startDay} - ${e.endDay}`]),
            theme: 'grid', headStyles: HEAD_STYLE, bodyStyles: BODY_STYLE, margin: MARGIN,
        });
        y = getY(doc) + 8;
    }

    // SIPs
    if (report.sipWindows.length > 0) {
        y = subHeader(doc, `SIPs (${report.sipWindows.length})`, y);
        autoTable(doc, {
            startY: y,
            head: [['Amount', 'Window', 'Counterparty']],
            body: report.sipWindows.map((e) => [
                fmt(e.averageAmount), `Day ${e.startDay} - ${e.endDay}`, e.counterparty || '-',
            ]),
            theme: 'grid', headStyles: HEAD_STYLE, bodyStyles: BODY_STYLE, margin: MARGIN,
        });
        y = getY(doc) + 8;
    }

    // ═══════════════════════════════════════
    // CASHFLOW TAB
    // ═══════════════════════════════════════
    y = sectionHeader(doc, 'Cashflow', y);

    // Monthly Inflow vs Outflow
    const inflowLen = report.monthlyInflowHistory.length;
    const outflowLen = report.monthlyOutflowHistory.length;
    if (inflowLen > 0 || outflowLen > 0) {
        y = subHeader(doc, 'Inflow vs Outflow', y + 4);
        const months = Math.max(inflowLen, outflowLen);
        const dualData: { label: string; inflow: number; outflow: number }[] = [];
        const tableRows: string[][] = [];
        for (let i = 0; i < months; i++) {
            const inf = report.monthlyInflowHistory[i];
            const out = report.monthlyOutflowHistory[i];
            const label = getMonthLabel(inf?.month || out?.month || '');
            const infAmt = inf?.amount ?? 0;
            const outAmt = out?.amount ?? 0;
            dualData.push({ label, inflow: infAmt, outflow: outAmt });
            tableRows.push([inf?.month ?? out?.month ?? '-', inf ? fmt(infAmt) : '-', out ? fmt(outAmt) : '-']);
        }
        y = drawDualBarChart(doc, y, dualData);
        autoTable(doc, {
            startY: y,
            head: [['Month', 'Inflow', 'Outflow']],
            body: tableRows,
            theme: 'grid', headStyles: HEAD_STYLE, bodyStyles: BODY_STYLE, margin: MARGIN,
        });
        y = getY(doc) + 8;
    }

    // Balance Trend
    if (report.monthlyBalanceHistory.length > 0) {
        y = subHeader(doc, 'End-of-Month Balance Trend', y);
        y = drawLineChart(
            doc, y,
            report.monthlyBalanceHistory.map(e => ({
                label: getMonthLabel(e.month || ''),
                value: e.amount ?? e.averageAmount ?? 0,
            })),
            [99, 102, 241],
        );
        autoTable(doc, {
            startY: y,
            head: [['Month', 'Balance']],
            body: report.monthlyBalanceHistory.map((e) => [e.month ?? '-', fmt(e.amount ?? 0)]),
            theme: 'grid', headStyles: HEAD_STYLE, bodyStyles: BODY_STYLE, margin: MARGIN,
        });
        y = getY(doc) + 8;
    }

    // ═══════════════════════════════════════
    // BEHAVIOR TAB
    // ═══════════════════════════════════════
    y = sectionHeader(doc, 'Behavior', y);

    // Phase 2: Spending Breakdown
    if (report.spendingBreakdown && report.spendingBreakdown.length > 0) {
        y = subHeader(doc, 'Spending Breakdown', y + 4);
        autoTable(doc, {
            startY: y,
            head: [['Category', 'Amount', '% of Income', 'Details']],
            body: report.spendingBreakdown.map((g) => [
                g.group,
                fmt(g.totalAmount),
                `${g.percentOfIncome.toFixed(1)}%`,
                g.categories.map(c => `${c.name}: ${fmt(c.amount)}`).join(', '),
            ]),
            theme: 'grid', headStyles: HEAD_STYLE, bodyStyles: BODY_STYLE, margin: MARGIN,
            columnStyles: { 3: { cellWidth: 60 } },
        });
        y = getY(doc) + 8;
    }

    // Bounce & Payment Indicators (kept from Phase 1)
    y = subHeader(doc, 'Bounce & Payment Indicators', y);
    autoTable(doc, {
        startY: y,
        head: [['EMI Bounces', 'NACH Bounces', 'SI Bounces', 'EMI Delays', 'Late Fee Charges', 'Return Charges']],
        body: [[
            String(report.emiBounces), String(report.nachBounces), String(report.siBounces),
            String(report.emiDelays), String(report.lateFeeCharges), String(report.returnCharges),
        ]],
        theme: 'grid',
        headStyles: { ...HEAD_STYLE, fontSize: 8 },
        bodyStyles: { ...BODY_STYLE, halign: 'center', fontStyle: 'bold' },
        margin: MARGIN,
    });
    y = getY(doc) + 8;

    // Phase 2: Bounce Events Log (detailed transactions)
    if (report.bounceLog && report.bounceLog.length > 0) {
        const allTxns = report.bounceLog.flatMap((entry) =>
            entry.transactions.map((txn) => ({
                ...txn,
                month: entry.month,
            }))
        );
        if (allTxns.length > 0) {
            y = subHeader(doc, 'Bounce Events Log', y);
            autoTable(doc, {
                startY: y,
                head: [['Date', 'Type', 'Amount', 'Reason', 'Severity']],
                body: allTxns.map((txn) => {
                    const severity = txn.amount >= 10000 ? 'Critical' : txn.amount >= 5000 ? 'High' : 'Medium';
                    return [txn.date || txn.month, txn.type, fmt(txn.amount), txn.narration || '-', severity];
                }),
                theme: 'grid', headStyles: HEAD_STYLE, bodyStyles: BODY_STYLE, margin: MARGIN,
            });
            y = getY(doc) + 8;
        }
    }

    // Phase 2: Repayment Heatmap (table form)
    if (report.repaymentHeatmap && report.repaymentHeatmap.length > 0) {
        y = subHeader(doc, 'Repayment Heatmap', y);
        const statusLabels: Record<string, string> = {
            'on-time': 'On-Time',
            'late': 'Late',
            'bounced': 'Bounced',
            'no-data': 'No Data',
        };
        autoTable(doc, {
            startY: y,
            head: [['Month', 'Status', 'EMI Count', 'Bounce Count']],
            body: report.repaymentHeatmap.map((e) => [
                getMonthLabel(e.month),
                statusLabels[e.status] || e.status,
                String(e.emiCount),
                String(e.bounceCount),
            ]),
            theme: 'grid', headStyles: HEAD_STYLE, bodyStyles: BODY_STYLE, margin: MARGIN,
        });
        y = getY(doc) + 8;
    }

    // Repayment Summary
    y = subHeader(doc, 'Repayment Summary', y);
    autoTable(doc, {
        startY: y,
        head: [['Metric', 'Value']],
        body: [
            ['Total Bounce Events', String(report.bounceEvents)],
            ['Active EMIs', String(report.activeEMIs)],
            ['FOIR', `${report.foir.toFixed(1)}%`],
        ],
        theme: 'grid', headStyles: HEAD_STYLE, bodyStyles: BODY_STYLE, margin: MARGIN,
    });

    // ─── Save ───
    const filename = `lending-report-${report.consentId.substring(0, 8)}-${new Date().toISOString().slice(0, 10)}.pdf`;
    doc.save(filename);
}
